import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { auditService, TestEvaluation } from '@services/auditService';
import { organizationService } from '@services/organizationService';
import TestEvaluationVerdictForm from '@components/audit/TestEvaluationVerdictForm';
import {
  detectContentCategoriesFromHtml,
  categoriesToHideAfterAutoDetect,
  NON_DETECTABLE_CATEGORIES,
} from '@utils/contentCategoryDetection';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';

/**
 * REV-358 (REV-56 first cut): manual testing checklist page.
 *
 * Lists every check_type='manual' TestEvaluation in an audit grouped by
 * content_category, with a top-of-page progress indicator and status
 * filter chips. Closes the biggest manual-checklist UX gap: today
 * reviewers cannot see what manual tests remain across the whole audit
 * without drilling into criteria one at a time.
 *
 * Verdict editing happens inline (REV-359) — clicking Evaluate or Update
 * on a row expands the shared TestEvaluationVerdictForm right there.
 * On save the row updates in place and progress recomputes. Drilling
 * into CriterionDetailPage is still useful for context (test methodology,
 * other tests on the same SC) but no longer required.
 */

type StatusFilter = 'all' | 'not_tested' | 'pass' | 'fail' | 'not_applicable';

const STATUS_FILTERS: Array<{ key: StatusFilter; labelKey: string; fallback: string }> = [
  { key: 'all', labelKey: 'manualChecklist.filter.all', fallback: 'All' },
  {
    key: 'not_tested',
    labelKey: 'manualChecklist.filter.notTested',
    fallback: 'Not yet tested',
  },
  { key: 'pass', labelKey: 'manualChecklist.filter.pass', fallback: 'Pass' },
  { key: 'fail', labelKey: 'manualChecklist.filter.fail', fallback: 'Fail' },
  {
    key: 'not_applicable',
    labelKey: 'manualChecklist.filter.notApplicable',
    fallback: 'N/A',
  },
];

// Keyed on the whole verdict union, so TypeScript requires an entry for every
// verdict a row can hold. 'needs_review' (REV-1072) should not reach THIS page
// — engineBridgeService only ever touches automatic and auto_assisted rows, and
// this is the manual checklist — but it is a verdict the type permits, and a
// missing entry would render `undefined` in a chip rather than fail loudly.
const VERDICT_LABEL: Record<TestEvaluation['verdict'], string> = {
  pass: 'Pass',
  fail: 'Fail',
  not_applicable: 'N/A',
  not_tested: 'Not tested',
  needs_review: 'Needs review',
};

const VERDICT_COLOR: Record<
  TestEvaluation['verdict'],
  'success' | 'error' | 'default' | 'warning'
> = {
  pass: 'success',
  fail: 'error',
  not_applicable: 'default',
  not_tested: 'warning',
  needs_review: 'warning',
};

export default function ManualChecklistPage() {
  const { t } = useTranslation();
  const { slug, auditId } = useParams<{ slug: string; auditId: string }>();

  const [orgId, setOrgId] = useState<string>('');
  const [tests, setTests] = useState<TestEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // REV-359: which row is currently in inline-edit mode. Only one form is
  // open at a time — clicking Evaluate on a different row replaces it.
  // recordVerdict returns the updated TestEvaluation so we patch the row
  // in place without refetching the whole list.
  const [editingId, setEditingId] = useState<string | null>(null);
  // REV-360: per-category visibility. Hidden categories' sections are not
  // rendered, but their tests still count toward audit-wide progress —
  // visibility ≠ N/A. Default empty (all visible).
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [autoDetectMessage, setAutoDetectMessage] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!slug || !auditId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Same slug→orgId resolution pattern as the other audit pages.
      // REV-802: through the API client, which carries and renews the
      // in-memory token; a raw fetch would do neither.
      const resolvedOrgId = (await organizationService.getBySlug(slug)).id;
      setOrgId(resolvedOrgId);

      // limit=500 covers any realistic WCAG 2.2 AA audit (~150-200 manual
      // tests). Pagination metadata is dropped by listTestEvaluations,
      // but we compute progress from the rows themselves.
      const data = await auditService.listTestEvaluations(resolvedOrgId, auditId, {
        check_type: 'manual',
        limit: 500,
      });
      setTests(data);
    } catch (err) {
      setError(
        toDisplayError(err, t('manualChecklist.error.loadFailed', 'Failed to load manual tests'))
      );
    } finally {
      setLoading(false);
    }
  }, [slug, auditId, t]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Aggregate progress: how many of the manual tests have a verdict set.
  // not_tested is the "to-do" state, and needs_review (REV-1072) is the engine
  // waiting on a person, so neither counts as completed.
  const progress = useMemo(() => {
    const total = tests.length;
    const completed = tests.filter(
      (te) => te.verdict !== 'not_tested' && te.verdict !== 'needs_review'
    ).length;
    const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
    return { total, completed, percent };
  }, [tests]);

  // Per-status counts for the filter chip labels — gives the reviewer
  // an at-a-glance read of "how many fails do I need to triage?"
  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> & Record<string, number> = {
      all: tests.length,
      not_tested: 0,
      pass: 0,
      fail: 0,
      not_applicable: 0,
    };
    for (const te of tests) {
      // A verdict with no chip (REV-1072's needs_review) is still counted
      // rather than dropped, so a row can never go missing from the totals
      // just because this page has no filter for its state.
      counts[te.verdict] = (counts[te.verdict] ?? 0) + 1;
    }
    return counts;
  }, [tests]);

  const filteredTests = useMemo(() => {
    if (statusFilter === 'all') {
      return tests;
    }
    return tests.filter((te) => te.verdict === statusFilter);
  }, [tests, statusFilter]);

  // The full set of categories present in the audit, regardless of the
  // status filter. Drives the visibility-chip row so the row count stays
  // stable when the reviewer switches between Pass/Fail/etc.
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const te of tests) {
      cats.add(te.content_category || 'Other');
    }
    return Array.from(cats).sort();
  }, [tests]);

  // Per-category total counts (for chip labels — reviewers see "Forms (12)"
  // and know how many tests they're hiding).
  const categoryTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const te of tests) {
      const cat = te.content_category || 'Other';
      map.set(cat, (map.get(cat) ?? 0) + 1);
    }
    return map;
  }, [tests]);

  const toggleCategory = useCallback((category: string) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
    setAutoDetectMessage(null);
  }, []);

  /**
   * Auto-detect: read the audit's automated FindingInstances, scan their
   * element_html for DOM signals, and hide categories whose signal isn't
   * present. Behavioral categories (Keyboard, CSS, etc.) stay visible —
   * they can't be statically detected so the reviewer has to decide.
   *
   * The message after running explains what changed; the reviewer always
   * has the per-category chip to override.
   */
  const handleAutoDetect = useCallback(async () => {
    if (!orgId || !auditId) {
      return;
    }
    setAutoDetecting(true);
    setAutoDetectMessage(null);
    try {
      // limit=200 keeps the heuristic input bounded but still gives a
      // representative sample for category detection. Real audits rarely
      // accumulate more findings than this in element-level snapshots.
      const findingsRes = await auditService.listFindings(orgId, auditId, { limit: 200 });
      const samples = findingsRes.data
        .map((f) => f.element_html)
        .filter((h): h is string => typeof h === 'string' && h.length > 0);
      const detected = detectContentCategoriesFromHtml(samples);
      const toHide = categoriesToHideAfterAutoDetect(allCategories, detected);
      setHiddenCategories(toHide);
      setAutoDetectMessage(
        t(
          'manualChecklist.autoDetect.result',
          'Auto-detect complete: {{detected}} categories detected from {{samples}} finding samples; {{hidden}} categories hidden as likely not applicable.',
          {
            detected: detected.size,
            samples: samples.length,
            hidden: toHide.size,
          }
        )
      );
    } catch (err) {
      setAutoDetectMessage(
        toDisplayError(err, t('manualChecklist.autoDetect.error', 'Auto-detect failed.'))
      );
    } finally {
      setAutoDetecting(false);
    }
  }, [orgId, auditId, allCategories, t]);

  // Group filtered rows by content_category so reviewers can work the
  // checklist topic by topic rather than by raw test_id order. Hidden
  // categories are dropped here so the table doesn't render their rows.
  const grouped = useMemo(() => {
    const map = new Map<string, TestEvaluation[]>();
    for (const te of filteredTests) {
      const cat = te.content_category || 'Other';
      if (hiddenCategories.has(cat)) {
        continue;
      }
      if (!map.has(cat)) {
        map.set(cat, []);
      }
      map.get(cat)!.push(te);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredTests, hiddenCategories]);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to={`/orgs/${slug}/audits/${auditId}`}
        sx={{ mb: 2 }}
      >
        {t('manualChecklist.backButton', 'Back to audit review')}
      </Button>

      <Typography variant="h4" gutterBottom>
        {t('manualChecklist.title', 'Manual testing checklist')}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <Box sx={{ minWidth: 240 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('manualChecklist.progress.label', 'Progress')}
            </Typography>
            <Typography variant="h6">
              {t('manualChecklist.progress.completed', '{{completed}} of {{total}} completed', {
                completed: progress.completed,
                total: progress.total,
              })}{' '}
              ({progress.percent}%)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.percent}
              sx={{ mt: 1, height: 8, borderRadius: 1 }}
              aria-label={t(
                'manualChecklist.progress.barAria',
                'Manual checklist completion progress'
              )}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {t('manualChecklist.filter.label', 'Filter by status')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {STATUS_FILTERS.map((f) => (
                <Chip
                  key={f.key}
                  label={`${t(f.labelKey, f.fallback)} (${statusCounts[f.key]})`}
                  color={statusFilter === f.key ? 'primary' : 'default'}
                  variant={statusFilter === f.key ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(f.key)}
                  // Filter chips are interactive — make them keyboard-friendly.
                  clickable
                />
              ))}
            </Stack>
          </Box>
        </Stack>

        {/* REV-360: per-category visibility row. Hidden categories are
            collapsed out of the table — useful when a product doesn't
            have, say, video or tables. Auto-detect runs a heuristic over
            the audit's findings to suggest which to hide. */}
        {allCategories.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t('manualChecklist.categories.label', 'Categories visible')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  autoDetecting ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AutoAwesomeIcon fontSize="small" />
                  )
                }
                disabled={autoDetecting}
                onClick={handleAutoDetect}
              >
                {autoDetecting
                  ? t('manualChecklist.autoDetect.running', 'Auto-detecting…')
                  : t('manualChecklist.autoDetect.button', 'Auto-detect from findings')}
              </Button>
              {hiddenCategories.size > 0 && (
                <Button
                  size="small"
                  onClick={() => {
                    setHiddenCategories(new Set());
                    setAutoDetectMessage(null);
                  }}
                >
                  {t('manualChecklist.categories.showAll', 'Show all')}
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {allCategories.map((cat) => {
                const visible = !hiddenCategories.has(cat);
                const count = categoryTotals.get(cat) ?? 0;
                const undetectable = NON_DETECTABLE_CATEGORIES.has(cat);
                return (
                  <Chip
                    key={cat}
                    label={`${cat} (${count})`}
                    color={visible ? 'primary' : 'default'}
                    variant={visible ? 'filled' : 'outlined'}
                    onClick={() => toggleCategory(cat)}
                    clickable
                    title={
                      undetectable
                        ? t(
                            'manualChecklist.categories.undetectableTitle',
                            'Behavioral category — auto-detect cannot infer applicability.'
                          )
                        : undefined
                    }
                  />
                );
              })}
            </Stack>
            {autoDetectMessage && (
              <Alert severity="info" sx={{ mt: 1 }} onClose={() => setAutoDetectMessage(null)}>
                {autoDetectMessage}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {progress.total === 0 && (
        <Alert severity="info">
          {t(
            'manualChecklist.empty.noManual',
            'This audit has no manual tests. Manual tests are derived from the WCAG SCs that require human verification.'
          )}
        </Alert>
      )}

      {progress.total > 0 && grouped.length === 0 && (
        <Alert severity="info">
          {t(
            'manualChecklist.empty.noMatch',
            'No manual tests match this filter. Try selecting "All" to see every manual test.'
          )}
        </Alert>
      )}

      {grouped.map(([category, rows]) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {category} ({rows.length})
          </Typography>
          <ScrollableTableContainer component={Paper} variant="outlined">
            <Table
              size="small"
              aria-label={t('manualChecklist.tableLabel', '{{category}} tests', { category })}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('manualChecklist.col.testId', 'Test')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('manualChecklist.col.wcag', 'WCAG SC')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('manualChecklist.col.verdict', 'Verdict')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('manualChecklist.col.note', 'Note')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {t('manualChecklist.col.actions', 'Actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((te) => {
                  const isEditing = editingId === te.id;
                  // When the inline form is open the row collapses to a
                  // single full-width cell so the form has room to breathe.
                  // The drill-in link to CriterionDetailPage is preserved
                  // for context (other tests on the same SC, methodology
                  // history) but is no longer the primary path to record
                  // a verdict.
                  if (isEditing) {
                    return (
                      <TableRow key={te.id}>
                        <TableCell colSpan={5}>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace' }}>
                              {te.test_id}
                              {te.wcag_criteria?.length
                                ? ` — ${t('manualChecklist.wcagCriteria', 'WCAG {{criteria}}', { criteria: te.wcag_criteria.join(', ') })}`
                                : ''}
                            </Typography>
                            <TestEvaluationVerdictForm
                              te={te}
                              orgId={orgId}
                              onSaved={(updated) => {
                                setTests((prev) =>
                                  prev.map((row) => (row.id === updated.id ? updated : row))
                                );
                                setEditingId(null);
                              }}
                              onCancel={() => setEditingId(null)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const primarySc = te.wcag_criteria?.[0];
                  return (
                    <TableRow key={te.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {te.test_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(te.wcag_criteria || []).map((sc) => (
                          <Chip key={sc} label={sc} size="small" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={VERDICT_LABEL[te.verdict]}
                          color={VERDICT_COLOR[te.verdict]}
                          size="small"
                          variant={te.verdict === 'not_tested' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={te.verdict_note || ''}
                        >
                          {te.verdict_note || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {orgId && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setEditingId(te.id)}
                            >
                              {te.verdict === 'not_tested'
                                ? t('manualChecklist.action.evaluate', 'Evaluate')
                                : t('manualChecklist.action.update', 'Update')}
                            </Button>
                          )}
                          {primarySc && (
                            <Button
                              size="small"
                              component={RouterLink}
                              to={`/orgs/${slug}/audits/${auditId}/criteria/${primarySc}`}
                            >
                              {t('manualChecklist.action.detail', 'Detail')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        </Box>
      ))}
    </Box>
  );
}
